import { serverSchema } from "./protocol.js";
const CLOSE_NORMAL = 1000;
let WebSocketP;
if (typeof window !== "undefined") {
    WebSocketP = Promise.resolve(window.WebSocket);
}
else {
    WebSocketP = (async () => {
        /** We have to choose a type, and we choose the native browser type.
         * So ignore that we don't have the @types/ws available. */
        // @ts-ignore
        const ws = await import("ws");
        return ws.default;
    })();
}
function promisePair() {
    let resolvePromise;
    const promise = new Promise((resolve) => {
        resolvePromise = resolve;
    });
    return { promise, resolve: resolvePromise };
}
/**
 * A wrapper around a websocket that handles errors, reconnection, and message
 * parsing.
 */
export class WebSocketManager {
    constructor(uri, onOpen, onMessage) {
        this.socket = { state: "disconnected", promisePair: promisePair() };
        this.initialBackoff = 100;
        this.maxBackoff = 16000;
        this.retries = 0;
        this.closedUnexpectedly = false;
        this.uri = uri;
        this.onOpen = onOpen;
        this.onMessage = onMessage;
        this.connect();
    }
    async connect() {
        if (this.socket.state === "closing") {
            return;
        }
        if (this.socket.state !== "disconnected") {
            throw new Error("Didn't start connection from disconnected state");
        }
        this.socket = {
            state: "waitingForImport",
            promisePair: this.socket.promisePair,
        };
        const WebSocket = await WebSocketP;
        this.socket = this.socket; // disregard narrowing after await
        if (this.socket.state === "disconnected")
            return;
        if (this.socket.state !== "waitingForImport") {
            throw new Error("Socket not waiting when import promise resolved");
        }
        const socket = new WebSocket(this.uri);
        this.socket = {
            state: "connecting",
            promisePair: this.socket.promisePair,
            ws: socket,
        };
        socket.onopen = () => {
            if (this.socket.state !== "connecting") {
                throw new Error("onopen called with socket not in connecting state");
            }
            this.socket.promisePair.resolve(null);
            this.socket = { state: "ready", ws: socket };
            if (!this.closedUnexpectedly) {
                this.retries = 0;
            }
            this.onOpen();
        };
        // NB: The WebSocket API calls `onclose` even if connection fails, so we can route all error paths through `onclose`.
        socket.onerror = (error) => {
            let message = error.message;
            console.log(`WebSocket error: ${message}`);
            socket.close();
        };
        socket.onmessage = (message) => {
            if (this.closedUnexpectedly) {
                this.closedUnexpectedly = false;
                this.retries = 0;
            }
            try {
                const serverMessage = serverSchema.parse(JSON.parse(message.data));
                this.onMessage(serverMessage);
            }
            catch (error) {
                console.log(`Failed to process server message, reconnecting: ${error}`);
                socket.close();
            }
        };
        socket.onclose = (event) => {
            if (event.code !== CLOSE_NORMAL) {
                let msg = `WebSocket closed unexpectedly with code ${event.code}`;
                if (event.reason) {
                    msg += `: ${event.reason}`;
                }
                console.error(msg);
                this.closedUnexpectedly = true;
            }
            if (this.socket.state === "closing") {
                this.socket.promisePair.resolve(null);
                this.socket = { state: "disconnected", promisePair: promisePair() };
                return;
            }
            this.socket = { state: "disconnected", promisePair: promisePair() };
            const backoff = this.nextBackoff();
            console.log(`Attempting reconnect in ${backoff}ms`);
            setTimeout(() => this.connect(), backoff);
        };
    }
    async waitForConnection() {
        if (this.socket.state === "waitingForImport" ||
            this.socket.state === "disconnected" ||
            this.socket.state === "connecting") {
            await this.socket.promisePair.promise;
            return;
        }
        if (this.socket.state === "closing") {
            throw new Error("Socket is currently closing");
        }
    }
    sendMessage(message) {
        if (this.socket.state === "ready") {
            const request = JSON.stringify(message);
            try {
                this.socket.ws.send(request);
            }
            catch (error) {
                console.log(`Failed to send message on WebSocket, reconnecting: ${error}`);
                this.socket.ws.close();
            }
        }
    }
    async close() {
        if (this.socket.state === "disconnected") {
            return;
        }
        else if (this.socket.state === "waitingForImport") {
            this.socket = { state: "disconnected", promisePair: promisePair() };
            return;
        }
        else if (this.socket.state === "connecting") {
            this.socket.ws.close();
            return;
        }
        else if (this.socket.state === "ready") {
            this.socket.ws.close();
            this.socket = { state: "closing", promisePair: promisePair() };
            await this.socket.promisePair.promise;
        }
        else {
            await this.socket.promisePair.promise;
        }
    }
    nextBackoff() {
        const baseBackoff = this.initialBackoff * Math.pow(2, this.retries);
        this.retries += 1;
        const actualBackoff = Math.min(baseBackoff, this.maxBackoff);
        const jitter = actualBackoff * (Math.random() - 0.5);
        return actualBackoff + jitter;
    }
}
//# sourceMappingURL=web_socket_manager.js.map